import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'

export interface MapMarker {
  id: string
  lat: number
  lng: number
  color?: string
  popup?: string
  onClick?: () => void
}

interface FleetMapProps {
  markers?: MapMarker[]
  center?: [number, number]
  zoom?: number
  height?: string
  className?: string
}

const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY as string | undefined

function buildStyleUrl(): string {
  if (MAPTILER_KEY) {
    return `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_KEY}`
  }
  // Fallback: OSM raster via demotiles (no key required)
  return 'https://demotiles.maplibre.org/style.json'
}

export function FleetMap({
  markers = [],
  center = [-63.18, -17.78],
  zoom = 11,
  height = '400px',
  className = '',
}: FleetMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markersRef = useRef<maplibregl.Marker[]>([])

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    mapRef.current = new maplibregl.Map({
      container: containerRef.current,
      style: buildStyleUrl(),
      center,
      zoom,
    })

    mapRef.current.addControl(new maplibregl.NavigationControl(), 'top-right')

    return () => {
      mapRef.current?.remove()
      mapRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync markers
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Remove old markers
    markersRef.current.forEach((m) => m.remove())
    markersRef.current = []

    markers.forEach((m) => {
      const el = document.createElement('div')
      el.className = 'w-4 h-4 rounded-full border-2 border-white shadow-md cursor-pointer'
      el.style.backgroundColor = m.color ?? '#2B6CFF'

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([m.lng, m.lat])
        .addTo(map)

      if (m.popup) {
        const popup = new maplibregl.Popup({ offset: 12 }).setHTML(
          `<div class="text-sm font-medium p-1">${m.popup}</div>`,
        )
        marker.setPopup(popup)
      }

      if (m.onClick) {
        el.addEventListener('click', m.onClick)
      }

      markersRef.current.push(marker)
    })
  }, [markers])

  return <div ref={containerRef} style={{ height }} className={`rounded-xl overflow-hidden ${className}`} />
}
